import { useContext, useState } from "react"
import ApiContext from "src/provider/API/call-service"
import { IoIosArrowBack } from "react-icons/io"
import { RiCloseFill } from "react-icons/ri"
import { AppLabel, AppSpan, AppText, Button, CustomContainer, FlexColumn, FlexRow, Form, FormGroup, SidePopupContainer } from "src/style"
import helpers from "src/utils/helpers"
import UploadIcon from 'src/assets/img/upload-icon.svg'
import { useNotifier } from "react-pop-notifier"

export const CreateNewServiceProvider: React.FC<any> = ({getProviders, close}) => {
    const {notifier} = useNotifier()
    const {API} = useContext(ApiContext)
    const [input, setInput] = useState({
        companyName: '', description: '', websiteURL: '',
        firstName: '', lastName: '', email: '',
        phoneNumber: '', username: '', logo: '',
        contactName: '', contactEmail: '', contactPhoneNumber: ''
    })
    const [logoExtra, setLogoExtra] = useState({
        logoName: '', logoUrl: '',logoSize: '', 
        logoExtension: '', logoContentType: ''
    })
    const [isAdminDetails, setIsAdminDetails] = useState(false)

    const handleInput = (e: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>) => setInput({...input, [e.target.name]: e.target.value})

    async function handleFile (e: React.ChangeEvent<HTMLInputElement>) {
        if (e.target.files instanceof FileList) {
            if(!helpers.isValidFileType(e.target.value)) return notifier.show('Only SVG, PNG, and JPEG files are allowed')

            const extension = helpers.getFileExtension(e.target.value)

            const uploadedFile = await helpers.compressImage(e.target.files[0])
            if (!uploadedFile) return notifier.show('Unable to compress large file size uploaded')

            helpers.convertFileToBase64(uploadedFile, true)
            .then(async (result: any) => {
                setInput({
                    ...input, 
                    [e.target.name]: result.baseURL,
                })

                setLogoExtra({
                    ...logoExtra, 
                    logoName: e.target.files![0]?.name,
                    logoUrl: URL.createObjectURL(e.target.files![0]),
                    logoSize: (e.target.files![0]?.size / 1024).toFixed(1),
                    logoExtension: extension,
                    logoContentType: result.contentType
                })
            })
            .catch(err => {
                notifier.show(JSON.stringify(err), 'Validation Error')
            })
        }
    }

    async function handleSubmit (e: React.FormEvent<HTMLButtonElement | HTMLFormElement>) {
        e.preventDefault()

        if (!isAdminDetails) return setIsAdminDetails(true) 

        const {
            companyName, websiteURL, description, firstName, 
            lastName, username, email, phoneNumber, logo, contactName, contactEmail, contactPhoneNumber
        } = input

        const payload: any = {
            companyName,
            websiteURL,
            description,
            contactName,
            contactEmail,
            contactPhoneNumber,
            adminUser: {
                firstName,
                lastName,
                username,
                email,
                phoneNumber
            },
            logo: {
                file: logo,
                contentType: logoExtra.logoContentType,
                fileName: logoExtra.logoName
            }
        }

        if (!input.logo) delete payload.logo

        const response = await API.CreateServiceProvider(payload)
        if (response) {
            getProviders()
            close()
        }
    }

    return (
        <>
            <SidePopupContainer zIndex="8" onClick={close} />
            <FlexColumn
                className="center-popup-container"
                justifyContent="flex-start"
                alignItems="flex-start" 
            >
                <FlexRow justifyContent='space-between' gap='2'>
                    <FlexRow width="auto" sizeUnit="">
                        { isAdminDetails &&
                            <IoIosArrowBack 
                                fontSize='2rem' fill='#667085'
                                style={{marginRight: '1rem'}}
                                onClick={() => setIsAdminDetails(false)}
                            />
                        }
                        <AppSpan color='#101828' textSize='1.8' fontWeight="600">Create New Service Provider</AppSpan>
                    </FlexRow>
                    <Button 
                        titleSize="2" fontWeight="500"
                        width='3.4' shadow='none'
                        height='3.4'
                        radius='0.4'
                        bgColor='#ffffff'
                        hoverBgColor='#FEF3F2'
                        borderColor='none'
                        color='#000000'
                        hoverColor='#B42318'
                        onClick={close}
                    >
                        <RiCloseFill style={{fontSize: '2.3rem'}} />
                    </Button> 
                </FlexRow>
                <Form onSubmit={handleSubmit} topMargin='2'>
                    { !isAdminDetails ?
                        <>
                            <CustomContainer bottomMargin="2">
                                <AppLabel color="#344054" fontWeight="500" bottomMargin="0.8">Service provider logo</AppLabel>
                                { !input.logo ?
                                    <FlexColumn 
                                        radius='0.8' minHeight='12.6' 
                                        padding='1.5' bgColor="#F7F8F9"
                                    >
                                        <img src={UploadIcon} />
                                        <input 
                                            type="file" name='logo' alt='logoExtension' 
                                            onChange={handleFile} 
                                            className="logo-input" 
                                            id="logo-id" 
                                        />
                                        <AppLabel color='#0A756F' fontWeight="500" topMargin='1' htmlFor='logo-id' align='center'>
                                            Click to upload
                                        </AppLabel>
                                        <AppSpan textSize='1.2' topMargin='0.3'>SVG preferred</AppSpan>
                                    </FlexColumn>
                                    :
                                    <CustomContainer 
                                        borderColor='#0D968F' radius='0.8'
                                        padding='1.5' minHeight='12.6'
                                        bgColor='transparent'
                                    >
                                        <FlexRow 
                                            justifyContent='space-between'
                                            gap='2' bottomMargin='1'
                                        >
                                            <CustomContainer width='6.8' height='3.1'>
                                                <img 
                                                    src={logoExtra.logoUrl} className='img-covered' 
                                                    style={{objectFit: 'contain', objectPosition: 'left'}} 
                                                />
                                            </CustomContainer>
                                            <AppSpan 
                                                hoverColor='#B42318' cursor='pointer'
                                                onClick={() => setInput({...input, logo: ''})}
                                            >
                                                <RiCloseFill fontSize='2rem' />
                                            </AppSpan>
                                        </FlexRow>
                                        <AppText fontWeight='600'>{logoExtra.logoName}</AppText>
                                        { logoExtra.logoSize &&
                                            <AppSpan color='#667085'>{logoExtra.logoSize} KB</AppSpan>
                                        }
                                    </CustomContainer>
                                }
                            </CustomContainer>
                            <FormGroup>
                                <label>Service provider name</label>
                                <input 
                                    placeholder="Enter provider name" 
                                    name='companyName'
                                    value={input.companyName}
                                    onChange={handleInput}
                                    required
                                />
                            </FormGroup>  
                            <FormGroup>
                                <label>Service provider website</label>
                                <input 
                                    placeholder="Enter provider website url" 
                                    name='websiteURL'
                                    value={input.websiteURL}
                                    onChange={handleInput}
                                />
                            </FormGroup>  
                            <FormGroup>
                                <label>Description</label>
                                <textarea 
                                    placeholder="Enter description" 
                                    name='description'
                                    value={input.description}
                                    onChange={handleInput}
                                />
                            </FormGroup> 
                            <FormGroup>
                                <label>Contact Name</label>
                                <input 
                                    placeholder="Enter contact name" 
                                    name='contactName'
                                    value={input.contactName}
                                    onChange={handleInput}
                                />
                            </FormGroup> 
                            {/* <FormGroup>
                                <label>Fee settlement account</label>
                                <input 
                                    placeholder="Enter fee settlement account" 
                                    name='feeSettlementAccount'
                                    value={input.feeSettlementAccount}
                                    onChange={handleInput}
                                    //required
                                />
                            </FormGroup>  */}
                        </>
                        :
                        <>
                            <FormGroup>
                                <label>Contact Email</label>
                                <input 
                                    placeholder="Enter contact email" 
                                    name='contactEmail'
                                    value={input.contactEmail}
                                    onChange={handleInput}
                                />
                            </FormGroup> 
                            <FormGroup>
                                <label>Contact Phone Number</label>
                                <input 
                                    placeholder="Enter contact phone number" 
                                    name='contactPhoneNumber'
                                    value={input.contactPhoneNumber}
                                    onChange={handleInput}
                                />
                            </FormGroup> 
                            <FormGroup>
                                <label>Admin first name</label>
                                <input 
                                    placeholder="Enter admin first name" 
                                    name='firstName'
                                    value={input.firstName}
                                    onChange={handleInput}
                                    required
                                />
                            </FormGroup>  
                            <FormGroup>
                                <label>Admin last name</label>
                                <input 
                                    placeholder="Enter admin last name" 
                                    name='lastName'
                                    value={input.lastName}
                                    onChange={handleInput}
                                    required
                                />
                            </FormGroup>  
                            <FormGroup>
                                <label>Admin username</label>
                                <input 
                                    placeholder="Enter admin username" 
                                    name='username'
                                    value={input.username}
                                    onChange={handleInput}
                                    required
                                />
                            </FormGroup>  
                            <FormGroup>
                                <label>Admin email</label>
                                <input 
                                    placeholder="Enter admin email" 
                                    name='email'
                                    value={input.email}
                                    onChange={handleInput}
                                    required
                                />
                            </FormGroup>  
                            <FormGroup>
                                <label>Admin phone number</label>
                                <input 
                                    placeholder="Enter admin phone number" 
                                    name='phoneNumber'
                                    value={input.phoneNumber}
                                    onChange={handleInput}
                                    required
                                />
                            </FormGroup>  
                        </>
                    } 

                    <FlexRow justifyContent='flex-end'>
                        <Button
                            width='8.1'
                            titleSize='1.4' fontWeight="500"
                            height='4' radius='0.8'
                            bottomMargin='1' hoverColor='#0D968F'
                            hoverBgColor='#fff' borderColor='#0D968F'
                        >
                            {isAdminDetails ? 'Submit' : 'Next'}
                        </Button>
                    </FlexRow>
                </Form>
            </FlexColumn>
        </>
    )
}

